import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { queryKeys } from "@/lib/queryKeys";

export type AssignmentStatus = "idle" | "assigning" | "success" | "error" | "not-cleaner";

interface AssignmentResult {
  success: boolean;
  propertyId?: string;
  error?: string;
}

export const useAssignCleaner = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AssignmentStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  /**
   * Get or create a cleaner profile for the current user
   */
  const getOrCreateCleanerForCurrentUser = async (): Promise<string | null> => {
    if (!user) return null;

    // Try to get existing cleaner profile
    const { data: cleaner, error: cleanerError } = await supabase
      .from("cleaners")
      .select("id")
      .eq("user_id", user.id)
      .maybeSingle();

    if (cleaner) {
      return cleaner.id;
    }

    // Create cleaner profile if it doesn't exist
    const { data: newCleaner, error: createError } = await supabase
      .from("cleaners")
      .insert({ user_id: user.id, name: user.email || "Unnamed Cleaner" })
      .select("id")
      .single();

    if (createError || !newCleaner) {
      console.error("Failed to create cleaner profile:", createError);
      return null;
    }

    return newCleaner.id;
  };

  /**
   * Assign the current user (as cleaner) to a property by ID
   * This is the shared function used by both QR and manual code flows
   */
  const assignCurrentCleanerToProperty = async (propertyId: string): Promise<AssignmentResult> => {
    // CRITICAL: Validate propertyId is provided and non-null
    if (!propertyId || propertyId === null || propertyId === undefined || propertyId.trim() === '') {
      const error = "CRITICAL: Attempted cleaner assignment without propertyId";
      console.error(error, { propertyId });
      throw new Error(error);
    }

    if (!user) {
      return { success: false, error: "Not authenticated" };
    }

    // Check if user is a cleaner
    if (profile?.role !== "cleaner") {
      setStatus("not-cleaner");
      return { success: false, error: "Only cleaners can be assigned to properties" };
    }

    setStatus("assigning");
    setError(null);

    try {
      // Step 1: Check if property has a cleaning company assigned
      // QR codes and Property Codes should ONLY work when NO cleaning company is assigned
      const { data: companyAssignment, error: companyCheckError } = await supabase
        .from("property_cleaning_assignments")
        .select("company_id")
        .eq("property_id", propertyId)
        .maybeSingle();

      if (companyCheckError) {
        console.error("Error checking company assignment:", companyCheckError);
        throw new Error("Failed to check property assignment. Please try again.");
      }

      // If cleaning company is assigned, block QR/Property Code assignment
      if (companyAssignment?.company_id) {
        setStatus("error");
        toast({
          title: "Assignment Blocked",
          description: "This property is managed by a cleaning company. Ask your company admin to assign you.",
          variant: "destructive",
        });
        return { 
          success: false, 
          error: "This property is managed by a cleaning company. Ask your company admin to assign you." 
        };
      }

      // 2. Get or create cleaner profile and fetch company_id
      const cleanerId = await getOrCreateCleanerForCurrentUser();
      if (!cleanerId) {
        throw new Error("Failed to get or create cleaner profile");
      }

      // Fetch cleaner row to check company_id
      const { data: cleaner, error: cleanerFetchError } = await supabase
        .from("cleaners")
        .select("id, company_id")
        .eq("id", cleanerId)
        .single();

      if (cleanerFetchError || !cleaner) {
        throw new Error("Failed to fetch cleaner profile");
      }

      // Block if cleaner belongs to a company AND property is company-managed
      // (This is redundant with the check above, but provides clearer error messaging)
      if (cleaner.company_id && companyAssignment?.company_id) {
        setStatus("error");
        toast({
          title: "Assignment Blocked",
          description: "This property is managed by a cleaning company. Ask your company admin to assign you.",
          variant: "destructive",
        });
        return { 
          success: false, 
          error: "This property is managed by a cleaning company. Ask your company admin to assign you." 
        };
      }

      // 3. FINAL VALIDATION: Ensure propertyId is still valid before insert
      if (!propertyId || propertyId === null || propertyId === undefined || propertyId.trim() === '') {
        throw new Error("CRITICAL: propertyId is null or empty before insert");
      }

      if (!cleanerId || cleanerId === null || cleanerId === undefined) {
        throw new Error("CRITICAL: cleanerId is null or empty before insert");
      }

      // 4. Upsert into cleaner_assignments (creates if doesn't exist, updates if exists)
      // CRITICAL: Both property_id and cleaner_id MUST be non-null
      const { error: assignError } = await supabase
        .from("cleaner_assignments")
        .upsert(
          { cleaner_id: cleanerId, property_id: propertyId },
          { onConflict: "property_id,cleaner_id" }
        );

      if (assignError) {
        throw new Error("Failed to assign to property. Please try again.");
      }

      // 4. Success - invalidate queries to ensure property is visible
      setStatus("success");
      
      // CRITICAL: Invalidate queries to ensure assignment and property are visible immediately
      await queryClient.invalidateQueries({ 
        queryKey: ["cleaner-assignment", propertyId] 
      });
      await queryClient.invalidateQueries({ 
        queryKey: ["property-cleaner", propertyId] 
      });
      await queryClient.invalidateQueries({ 
        queryKey: queryKeys.cleaners.assignments(propertyId) 
      });
      // Force refetch property data so cleaner can see property details immediately
      await queryClient.refetchQueries({ 
        queryKey: ["property-cleaner", propertyId] 
      });
      
      // 5. Redirect to cleaner portal
      setTimeout(() => {
        navigate(`/clean/${propertyId}`, { replace: true });
      }, 1000);

      return { success: true, propertyId };
    } catch (err) {
      console.error("Error assigning cleaner:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to assign to property";
      setError(errorMessage);
      setStatus("error");
      
      toast({
        title: "Assignment Failed",
        description: errorMessage,
        variant: "destructive",
      });

      return { success: false, error: errorMessage };
    }
  };

  /**
   * Assign cleaner using a property code (manual entry flow)
   * Uses SECURITY DEFINER RPC function for server-side validation and assignment
   */
  const assignCurrentCleanerByCode = async (code: string): Promise<AssignmentResult> => {
    if (!user) {
      return { success: false, error: "Not authenticated" };
    }

    // Check if user is a cleaner
    if (profile?.role !== "cleaner") {
      setStatus("not-cleaner");
      return { success: false, error: "Only cleaners can be assigned to properties" };
    }

    setStatus("assigning");
    setError(null);

    try {
      // Normalize code (function will handle uppercase conversion)
      const normalizedCode = code.trim();

      // Call the SECURITY DEFINER RPC function
      // This function handles all validation and assignment server-side
      const { data: propertyId, error: rpcError } = await supabase.rpc(
        "assign_cleaner_to_property_by_code",
        { p_property_code: normalizedCode }
      );

      if (rpcError) {
        // The function returns user-friendly error messages
        const errorMessage = rpcError.message || "Failed to assign to property. Please try again.";
        setStatus("error");
        toast({
          title: "Assignment Failed",
          description: errorMessage,
          variant: "destructive",
        });
        return { success: false, error: errorMessage };
      }

      // CRITICAL: Validate propertyId returned from RPC
      if (!propertyId || propertyId === null || propertyId === undefined || propertyId === '') {
        const error = "CRITICAL: RPC function returned null or empty propertyId";
        console.error(error, { code: normalizedCode, propertyId });
        setStatus("error");
        toast({
          title: "Assignment Failed",
          description: "Invalid property code: Property not found or not available.",
          variant: "destructive",
        });
        return { success: false, error: "Property not found" };
      }

      // CRITICAL: Invalidate queries to ensure assignment is visible immediately
      // This ensures the cleaner_assignments row is visible before redirect
      // Invalidate for both cleaner and owner views
      await queryClient.invalidateQueries({ 
        queryKey: ["cleaner-assignment", propertyId] 
      });
      await queryClient.invalidateQueries({ 
        queryKey: ["property-cleaner", propertyId] 
      });
      await queryClient.invalidateQueries({ 
        queryKey: queryKeys.cleaners.assignments(propertyId) 
      });
      // Also invalidate all cleaner assignment queries (for owner's property settings)
      await queryClient.invalidateQueries({ 
        queryKey: ["cleaner-assignments"] 
      });
      // Force refetch to ensure data is fresh (assignment + property)
      await queryClient.refetchQueries({ 
        queryKey: queryKeys.cleaners.assignments(propertyId) 
      });
      // Refetch property data so cleaner can see property details immediately
      await queryClient.refetchQueries({ 
        queryKey: ["property-cleaner", propertyId] 
      });

      // Success - assignment completed via RPC
      setStatus("success");
      
      // Small delay to ensure queries are invalidated, then redirect
      setTimeout(() => {
        navigate(`/clean/${propertyId}`, { replace: true });
      }, 500);

      return { success: true, propertyId };
    } catch (err) {
      console.error("Error in assignCurrentCleanerByCode:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to process property code";
      setError(errorMessage);
      setStatus("error");
      
      toast({
        title: "Assignment Failed",
        description: errorMessage,
        variant: "destructive",
      });

      return { success: false, error: errorMessage };
    }
  };

  const resetStatus = () => {
    setStatus("idle");
    setError(null);
  };

  return {
    status,
    error,
    assignCurrentCleanerToProperty,
    assignCurrentCleanerByCode,
    resetStatus,
  };
};
